import { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  FileDown, Loader2, RefreshCw, PlayCircle, CheckCircle2, XCircle, Clock,
  Trash2, Library, FileText, ShieldAlert, ClipboardCheck, BarChart3, Layers,
  FileSignature, AlertTriangle,
} from 'lucide-react';

const ICONS = {
  soa: FileSignature,
  gap_report: ShieldAlert,
  risk_register: AlertTriangle,
  risk_treatment_plan: ClipboardCheck,
  management_review: BarChart3,
  evidence_report: FileText,
  framework_compliance: Layers,
};

const STATUS_STYLE = {
  queued: { icon: Clock, className: 'bg-slate-100 text-slate-700 border-slate-200', label: 'Queued' },
  generating: { icon: Loader2, className: 'bg-amber-100 text-amber-800 border-amber-200 animate-none', label: 'Generating' },
  completed: { icon: CheckCircle2, className: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Completed' },
  failed: { icon: XCircle, className: 'bg-red-100 text-red-800 border-red-200', label: 'Failed' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.queued;
  const Icon = s.icon;
  return (
    <Badge className={`${s.className} border text-xs font-medium inline-flex items-center gap-1`} data-testid={`report-status-${status}`}>
      <Icon className={`w-3 h-3 ${status === 'generating' ? 'animate-spin' : ''}`} />
      {s.label}
    </Badge>
  );
}

function GenerateCard({ meta, frameworks, onGenerate, submitting, isAdmin }) {
  const Icon = ICONS[meta.key] || FileText;
  const [fw, setFw] = useState(frameworks[0]?.id || '');
  return (
    <Card className="border-slate-200 hover:border-orange-300 transition-colors" data-testid={`report-card-${meta.key}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-orange-50 p-2 border border-orange-100">
            <Icon className="w-5 h-5 text-orange-600" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-900">{meta.name}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {meta.needs_framework ? 'Choose a framework, then generate' : 'Generated from current state'}
            </div>
          </div>
        </div>
        {meta.needs_framework && (
          <Select value={fw} onValueChange={setFw}>
            <SelectTrigger className="h-8 text-xs" data-testid={`report-fw-select-${meta.key}`}>
              <SelectValue placeholder="Select framework" />
            </SelectTrigger>
            <SelectContent>
              {frameworks.map((f) => (
                <SelectItem key={f.id} value={f.id} data-testid={`report-fw-option-${f.id}`}>
                  {f.short_name || f.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => onGenerate(meta.key, meta.needs_framework ? fw : null, 'pdf')}
            disabled={submitting || (meta.needs_framework && !fw)}
            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
            data-testid={`report-generate-btn-${meta.key}`}
          >
            <PlayCircle className="w-4 h-4 mr-1.5" />
            PDF
          </Button>
          {isAdmin && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onGenerate(meta.key, meta.needs_framework ? fw : null, 'docx')}
              disabled={submitting || (meta.needs_framework && !fw)}
              className="flex-1 border-orange-300 text-orange-700 hover:bg-orange-50"
              data-testid={`report-generate-docx-btn-${meta.key}`}
              title="Generate as Word document (admins only)"
            >
              <FileText className="w-4 h-4 mr-1.5" />
              Word
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function ReportsHubPage() {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const [types, setTypes] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workerRunning, setWorkerRunning] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const loadTypes = useCallback(async () => {
    try {
      const [tRes, fRes] = await Promise.all([
        axios.get(`${API}/report-jobs/types`),
        axios.get(`${API}/frameworks-v2`),
      ]);
      setTypes(tRes.data.types || []);
      setWorkerRunning(!!tRes.data.worker_running);
      setFrameworks(Array.isArray(fRes.data) ? fRes.data : (fRes.data.frameworks || []));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report types');
    }
  }, []);

  const loadJobs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/report-jobs?limit=30`);
      setJobs(r.data.jobs || []);
      setWorkerRunning(!!r.data.worker_running);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTypes();
    loadJobs();
  }, [loadTypes, loadJobs]);

  // Poll every 3s while there are queued or generating jobs
  useEffect(() => {
    const hasActive = jobs.some((j) => j.status === 'queued' || j.status === 'generating');
    if (!hasActive) return;
    const t = setInterval(loadJobs, 3000);
    return () => clearInterval(t);
  }, [jobs, loadJobs]);

  const handleGenerate = async (reportType, frameworkId, fmt = 'pdf') => {
    setSubmitting(true);
    try {
      const payload = { report_type: reportType, format: fmt };
      if (frameworkId) payload.framework_id = frameworkId;
      const r = await axios.post(`${API}/report-jobs`, payload);
      toast.success(`Queued: ${r.data.label} (${fmt.toUpperCase()})`);
      loadJobs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to queue report');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (job) => {
    try {
      const r = await axios.get(`${API}/report-jobs/${job.id}/download`, { responseType: 'blob' });
      const fmt = job.format || 'pdf';
      const mime = fmt === 'docx'
        ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        : 'application/pdf';
      const url = window.URL.createObjectURL(new Blob([r.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(job.label || 'report').replace(/[^A-Za-z0-9_\- ]/g, '_')}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download failed');
    }
  };

  const handleRetry = async (job) => {
    try {
      await axios.post(`${API}/report-jobs/${job.id}/retry`);
      toast.success('Requeued');
      loadJobs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Retry failed');
    }
  };

  const handleDelete = async (job) => {
    try {
      await axios.delete(`${API}/report-jobs/${job.id}`);
      toast.success('Deleted');
      loadJobs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-hub-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Library className="w-4 h-4" /><span>Compliance Engine v2</span>
            <span className="text-slate-300">/</span><span>Reports</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <FileDown className="w-7 h-7 text-orange-600" /> Reports Hub
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Generate branded PDF reports asynchronously — the app remains usable while reports render in the background.
          </p>
          <div className="text-xs mt-1 flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 ${workerRunning ? 'text-emerald-600' : 'text-red-600'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${workerRunning ? 'bg-emerald-500' : 'bg-red-500'}`} />
              {workerRunning ? 'Background worker online' : 'Background worker offline'}
            </span>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={loadJobs} data-testid="reports-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Generate cards */}
      <div>
        <h2 className="text-base font-semibold text-slate-900 mb-3">Generate a report</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {types.map((meta) => (
            <GenerateCard
              key={meta.key}
              meta={meta}
              frameworks={frameworks}
              onGenerate={handleGenerate}
              submitting={submitting}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      </div>

      {/* Job history */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-slate-900">Recent reports</h2>
          <span className="text-xs text-slate-500" data-testid="reports-jobs-count">{jobs.length} shown</span>
        </div>
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
              </div>
            ) : jobs.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm" data-testid="reports-empty">
                No reports yet. Click Generate above to queue one.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {jobs.map((job) => {
                  const Icon = ICONS[job.report_type] || FileText;
                  return (
                    <div key={job.id} className="flex items-center gap-3 px-4 py-3" data-testid={`report-job-${job.id}`}>
                      <div className="rounded-md bg-slate-50 p-2 border border-slate-200">
                        <Icon className="w-4 h-4 text-slate-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-2">
                          {job.label}
                          <Badge className="bg-slate-100 text-slate-700 border-slate-200 border text-[10px] font-medium uppercase tracking-wide">
                            {job.format || 'pdf'}
                          </Badge>
                        </div>
                        <div className="text-xs text-slate-500">
                          {job.created_by || 'unknown'} · {new Date(job.created_at).toLocaleString()}
                          {job.file_size && ` · ${(job.file_size / 1024).toFixed(1)} KB`}
                          {job.error && <span className="text-red-600 ml-2">· {job.error}</span>}
                        </div>
                      </div>
                      <StatusBadge status={job.status} />
                      <div className="flex items-center gap-1">
                        {job.status === 'completed' && (
                          <Button size="sm" variant="outline" onClick={() => handleDownload(job)} data-testid={`report-download-${job.id}`}>
                            <FileDown className="w-4 h-4 mr-1" /> Download
                          </Button>
                        )}
                        {(job.status === 'failed' || job.status === 'completed') && (
                          <Button size="sm" variant="ghost" onClick={() => handleRetry(job)} data-testid={`report-retry-${job.id}`}>
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        )}
                        {job.status !== 'generating' && (
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(job)} data-testid={`report-delete-${job.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
